import { NotificationSettings, NotificationSettingModel, DEFAULT_NOTIFICATION_SETTINGS } from '../types/notifications';
import { apiService } from '../lib/api';

class NotificationService {
  private readonly STORAGE_KEY = 'chime_notification_settings';

  // Load notification settings from backend
  async loadNotificationSettings(): Promise<NotificationSettings> {
    try {
      const response = await apiService.request<NotificationSettings>('/api/v1/user/notification_preferences');
      // Save to localStorage for offline access
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(response));
      return response;
    } catch (error) {
      console.warn('Failed to load notification settings from API, using local storage:', error);
      return this.getNotificationSettings();
    }
  }

  // Get notification settings from localStorage (sync)
  getNotificationSettings(): NotificationSettings {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all properties exist
        return { ...DEFAULT_NOTIFICATION_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load notification settings from storage:', error);
    }
    return DEFAULT_NOTIFICATION_SETTINGS;
  }

  // Update a single notification setting
  async updateNotificationSetting(settingModel: NotificationSettingModel): Promise<void> {
    try {
      // Send to API first
      const updatedSettings = await this.sendToAPI(settingModel);

      // Update localStorage with response from API
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedSettings));
      console.log(`✅ Notification setting updated: ${settingModel.setting} = ${settingModel.enabled}`);
    } catch (error) {
      console.error('❌ Failed to save notification setting to API:', error);

      // Fallback to local storage only
      const currentSettings = this.getNotificationSettings();
      const updatedSettings = {
        ...currentSettings,
        [settingModel.setting]: settingModel.enabled,
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedSettings));

      throw error;
    }
  }

  // Update multiple notification settings
  async updateNotificationSettings(settings: Partial<NotificationSettings>): Promise<void> {
    try {
      // Send to API first
      const updatedSettings = await this.sendBulkToAPI(settings);

      // Update localStorage with response from API
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedSettings));
      console.log('✅ Notification settings updated:', settings);
    } catch (error) {
      console.error('❌ Failed to save notification settings to API:', error);

      // Fallback to local storage only
      const currentSettings = this.getNotificationSettings();
      const updatedSettings = { ...currentSettings, ...settings };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedSettings));

      throw error;
    }
  }

  // Reset to default settings
  async resetNotificationSettings(): Promise<void> {
    try {
      // Reset on server
      await apiService.request('/api/v1/user/update_notification_preferences', {
        method: 'POST',
        body: JSON.stringify(DEFAULT_NOTIFICATION_SETTINGS),
      });

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(DEFAULT_NOTIFICATION_SETTINGS));
      console.log('✅ Notification settings reset to defaults');
    } catch (error) {
      console.error('❌ Failed to reset notification settings on API:', error);

      // Fallback to local storage only
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(DEFAULT_NOTIFICATION_SETTINGS));
      throw error;
    }
  }

  // Send single setting update to API
  private async sendToAPI(settingModel: NotificationSettingModel): Promise<NotificationSettings> {
    // Convert the single setting to the format expected by the backend
    const payload = {
      [settingModel.setting]: settingModel.enabled
    };

    const response = await apiService.request<NotificationSettings>('/api/v1/user/update_notification_preferences', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return response;
  }

  // Send bulk settings update to API
  private async sendBulkToAPI(settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
    const response = await apiService.request<NotificationSettings>('/api/v1/user/update_notification_preferences', {
      method: 'POST',
      body: JSON.stringify(settings),
    });

    return response;
  }

  // Request notification permission (for browser notifications)
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }

  // Show a browser notification
  showNotification(title: string, options?: NotificationOptions): boolean {
    if (Notification.permission !== 'granted') {
      return false;
    }

    try {
      new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options,
      });
      return true;
    } catch (error) {
      console.error('Failed to show notification:', error);
      return false;
    }
  }
}

export const notificationService = new NotificationService();